import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { softwareList, softwareCategories } from '@/mocks/softwareData';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`text-xs ${
            star <= Math.floor(rating)
              ? 'ri-star-fill text-primary-500'
              : star - 0.5 <= rating
              ? 'ri-star-half-line text-primary-500'
              : 'ri-star-line text-foreground-300'
          }`}
        ></i>
      ))}
    </div>
  );
}

export default function CategoryPage() {
  const [activeCategory, setActiveCategory] = useState('全部');
  const [sortBy, setSortBy] = useState('popular');

  const filtered = useMemo(() => {
    let result = activeCategory === '全部' ? [...softwareList] : softwareList.filter((s) => s.category === activeCategory);
    if (sortBy === 'popular') result.sort((a, b) => parseInt(b.downloads.replace(/[万,]/g, '')) - parseInt(a.downloads.replace(/[万,]/g, '')));
    if (sortBy === 'rating') result.sort((a, b) => b.rating - a.rating);
    if (sortBy === 'newest') result.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    return result;
  }, [activeCategory, sortBy]);

  return (
    <main className="pt-16">
      {/* Breadcrumb */}
      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium">软件分类</span>
          </nav>
        </div>
      </div>

      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8 pb-6">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">软件分类</h1>
        <p className="text-sm text-foreground-500 mt-2">浏览全部软件资源，按分类筛选你需要的应用</p>
      </div>

      {/* Category Tabs */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mb-6">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
          {softwareCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                activeCategory === cat
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Sort + Results */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-foreground-500">
            共找到 <strong className="text-foreground-800">{filtered.length}</strong> 款软件
          </p>
          <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
            {[
              { key: 'popular', label: '最热' },
              { key: 'rating', label: '评分' },
              { key: 'newest', label: '最新' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === s.key ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((item) => (
            <div
              key={item.id}
              className="group bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-primary-200 transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 group-hover:bg-primary-100 transition-colors">
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-base font-semibold text-foreground-950 whitespace-nowrap">{item.name}</h3>
                    {item.isNew && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-accent-100 text-accent-700 rounded-md whitespace-nowrap">新品</span>
                    )}
                  </div>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{item.description}</p>
                  <div className="flex items-center gap-4 text-xs text-foreground-400 flex-wrap">
                    <span className="flex items-center gap-1"><i className="ri-folder-line"></i>{item.category}</span>
                    <span className="flex items-center gap-1"><i className="ri-hard-drive-line"></i>{item.size}</span>
                    <span className="flex items-center gap-1"><i className="ri-download-2-line"></i>{item.downloads}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <StarRating rating={item.rating} />
                    <span className="text-xs font-medium text-primary-500 whitespace-nowrap">{item.rating}分</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center gap-3">
                <button className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-download-line text-base"></i>高速下载
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-500 hover:text-foreground-700 hover:border-background-300 transition-colors cursor-pointer">
                  <i className="ri-heart-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300 mb-4">
              <i className="ri-inbox-line text-2xl"></i>
            </div>
            <p className="text-foreground-500 text-sm">该分类暂无软件</p>
          </div>
        )}
      </div>
    </main>
  );
}